/**
 * Support Mode Context
 *
 * Manages org impersonation ("act as org") for admin support tools.
 * Sessions expire after 30 minutes and all actions are audit logged.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Org, SupportSession } from '@/integrations/firebase/types';
import { logAdminAction } from '@/lib/admin/auditLog';

const SUPPORT_SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

interface SupportModeContextType {
  // Session state
  isInSupportMode: boolean;
  actingOrg: Org | null;
  actingOrgId: string | null;
  supportSession: SupportSession | null;

  // Expiration
  expiresAt: Date | null;
  timeRemainingMs: number | null;
  isExpired: boolean;

  // Write actions (disabled by default)
  writeActionsEnabled: boolean;

  // Actions
  enterSupportMode: (orgId: string) => Promise<{ success: boolean; error?: string }>;
  exitSupportMode: () => Promise<void>;
  enableWriteActions: () => Promise<void>;
  disableWriteActions: () => void;
  extendSession: () => Promise<void>;

  // Loading
  isLoading: boolean;
}

const SupportModeContext = createContext<SupportModeContextType | undefined>(undefined);

export function SupportModeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { adminUser, canImpersonate } = useAdminAuth();

  const [supportSession, setSupportSession] = useState<SupportSession | null>(null);
  const [actingOrg, setActingOrg] = useState<Org | null>(null);
  const [writeActionsEnabled, setWriteActionsEnabled] = useState(false);
  const [timeRemainingMs, setTimeRemainingMs] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Computed values
  // Only consider in support mode if we have both the session AND the org data loaded
  const isInSupportMode = !!supportSession && !supportSession.endedAt && !!actingOrg;
  const actingOrgId = supportSession?.orgId || null;
  const expiresAt = supportSession?.expiresAt
    ? supportSession.expiresAt.toDate()
    : null;
  const isExpired = expiresAt ? new Date() > expiresAt : false;

  // Load existing session on mount
  useEffect(() => {
    const loadExistingSession = async () => {
      if (!user?.uid || !adminUser) {
        setSupportSession(null);
        setActingOrg(null);
        setIsLoading(false);
        return;
      }

      try {
        // Check for active session
        const sessionsRef = collection(db, 'support_sessions');
        const q = query(
          sessionsRef,
          where('adminUserId', '==', user.uid),
          where('endedAt', '==', null)
        );
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const sessionDoc = snapshot.docs[0];
          const sessionData = sessionDoc.data() as SupportSession;

          // Check if expired
          const expires = sessionData.expiresAt.toDate();
          if (new Date() < expires) {
            setSupportSession({ ...sessionData, id: sessionDoc.id });
            setWriteActionsEnabled(sessionData.writeActionsEnabled);

            // Fetch the org
            const orgDoc = await getDoc(doc(db, 'orgs', sessionData.orgId));
            if (orgDoc.exists()) {
              setActingOrg({ id: orgDoc.id, ...orgDoc.data() } as Org);
            }
          } else {
            // Session expired, end it
            await updateDoc(doc(db, 'support_sessions', sessionDoc.id), {
              endedAt: serverTimestamp(),
              endReason: 'expired',
            });
          }
        }
      } catch (error) {
        console.error('Error loading support session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadExistingSession();
  }, [user?.uid, adminUser]);

  // Update time remaining every second
  useEffect(() => {
    if (!expiresAt || !isInSupportMode) {
      setTimeRemainingMs(null);
      return;
    }

    const updateRemaining = () => {
      const remaining = expiresAt.getTime() - Date.now();
      setTimeRemainingMs(Math.max(0, remaining));

      // Auto-exit when expired
      if (remaining <= 0) {
        exitSupportMode();
      }
    };

    updateRemaining();
    const interval = setInterval(updateRemaining, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, isInSupportMode]);

  // Enter support mode
  const enterSupportMode = useCallback(
    async (orgId: string): Promise<{ success: boolean; error?: string }> => {
      if (!user?.uid || !adminUser) {
        return { success: false, error: 'Not authenticated as admin' };
      }

      if (!canImpersonate) {
        return { success: false, error: 'Insufficient permissions to impersonate' };
      }

      try {
        setIsLoading(true);

        // Fetch the org
        const orgDoc = await getDoc(doc(db, 'orgs', orgId));
        if (!orgDoc.exists()) {
          return { success: false, error: 'Organization not found' };
        }

        const org = { id: orgDoc.id, ...orgDoc.data() } as Org;

        // End any existing session first
        if (supportSession && !supportSession.endedAt) {
          await updateDoc(doc(db, 'support_sessions', supportSession.id), {
            endedAt: serverTimestamp(),
            endReason: 'manual',
          });
        }

        // Create new session
        const sessionId = `${user.uid}_${Date.now()}`;
        const now = new Date();
        const expires = new Date(now.getTime() + SUPPORT_SESSION_DURATION_MS);

        const newSession: Omit<SupportSession, 'id'> = {
          adminUserId: user.uid,
          adminEmail: adminUser.email,
          orgId: org.id,
          orgName: org.name || org.id,
          startedAt: Timestamp.fromDate(now),
          expiresAt: Timestamp.fromDate(expires),
          writeActionsEnabled: false,
        };

        await setDoc(doc(db, 'support_sessions', sessionId), newSession);

        // Log the action
        await logAdminAction({
          actorUserId: user.uid,
          actorEmail: adminUser.email,
          actorRole: adminUser.adminRole,
          actionType: 'IMPERSONATE_ORG',
          targetOrgId: org.id,
          description: `Entered support mode for ${org.name || org.id}`,
        });

        setSupportSession({ id: sessionId, ...newSession });
        setActingOrg(org);
        setWriteActionsEnabled(false);

        return { success: true };
      } catch (error: any) {
        console.error('Error entering support mode:', error);
        return { success: false, error: error.message || 'Failed to enter support mode' };
      } finally {
        setIsLoading(false);
      }
    },
    [user?.uid, adminUser, canImpersonate, supportSession]
  );

  // Exit support mode
  const exitSupportMode = useCallback(async () => {
    if (!supportSession || !user?.uid || !adminUser) return;

    try {
      await updateDoc(doc(db, 'support_sessions', supportSession.id), {
        endedAt: serverTimestamp(),
        endReason: 'manual',
      });

      // Log the action
      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: 'EXIT_IMPERSONATION',
        targetOrgId: supportSession.orgId,
        description: `Exited support mode for ${supportSession.orgName}`,
      });

      setSupportSession(null);
      setActingOrg(null);
      setWriteActionsEnabled(false);
    } catch (error) {
      console.error('Error exiting support mode:', error);
    }
  }, [supportSession, user?.uid, adminUser]);

  // Enable write actions
  const enableWriteActions = useCallback(async () => {
    if (!supportSession || !user?.uid) return;

    try {
      await updateDoc(doc(db, 'support_sessions', supportSession.id), {
        writeActionsEnabled: true,
      });

      setWriteActionsEnabled(true);
      setSupportSession((prev) =>
        prev ? { ...prev, writeActionsEnabled: true } : null
      );
    } catch (error) {
      console.error('Error enabling write actions:', error);
    }
  }, [supportSession, user?.uid]);

  // Disable write actions
  const disableWriteActions = useCallback(() => {
    setWriteActionsEnabled(false);
    if (supportSession) {
      updateDoc(doc(db, 'support_sessions', supportSession.id), {
        writeActionsEnabled: false,
      }).catch(console.error);
    }
  }, [supportSession]);

  // Extend session by another 30 minutes
  const extendSession = useCallback(async () => {
    if (!supportSession || !user?.uid) return;

    try {
      const newExpires = new Date(Date.now() + SUPPORT_SESSION_DURATION_MS);

      await updateDoc(doc(db, 'support_sessions', supportSession.id), {
        expiresAt: Timestamp.fromDate(newExpires),
      });

      setSupportSession((prev) =>
        prev ? { ...prev, expiresAt: Timestamp.fromDate(newExpires) } : null
      );
    } catch (error) {
      console.error('Error extending session:', error);
    }
  }, [supportSession, user?.uid]);

  const value: SupportModeContextType = {
    isInSupportMode,
    actingOrg,
    actingOrgId,
    supportSession,
    expiresAt,
    timeRemainingMs,
    isExpired,
    writeActionsEnabled,
    enterSupportMode,
    exitSupportMode,
    enableWriteActions,
    disableWriteActions,
    extendSession,
    isLoading,
  };

  return (
    <SupportModeContext.Provider value={value}>
      {children}
    </SupportModeContext.Provider>
  );
}

export function useSupportMode() {
  const context = useContext(SupportModeContext);
  if (context === undefined) {
    throw new Error('useSupportMode must be used within a SupportModeProvider');
  }
  return context;
}
